import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    IconButton,
    Paper,
    Grid,
    FormControlLabel,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const CONTRIBUTION_TYPES = {
    MONETARY: 'monetary',
    IN_KIND: 'in_kind'
};

const PRICE_RANGES = [
    { value: 'up_to_1000', label: 'Up to $1,000' },
    { value: '1000_5000', label: '$1,000 - $5,000' },
    { value: '5000_10000', label: '$5,000 - $10,000' },
    { value: '10000_25000', label: '$10,000 - $25,000' },
    { value: '25000_50000', label: '$25,000 - $50,000' },
    { value: '50000_100000', label: '$50,000 - $100,000' },
    { value: '100000_plus', label: '$100,000+' },
    { value: 'custom', label: 'Custom Range' }
];

const SponsorTierForm = ({ tiers, onTiersChange }) => {
    const [newTier, setNewTier] = useState({
        name: '',
        contributionType: '',
        priceRange: '',
        customPriceRange: '',
        inKindDetails: '',
        customBenefits: '',
        isNegotiable: false
    });

    const handleAddTier = () => {
        if (!newTier.name.trim() || !newTier.contributionType) {
            return;
        }

        onTiersChange([...tiers, newTier]);
        setNewTier({
            name: '',
            contributionType: '',
            priceRange: '',
            customPriceRange: '',
            inKindDetails: '',
            customBenefits: '',
            isNegotiable: false
        });
    };

    const handleRemoveTier = (index) => {
        const updatedTiers = tiers.filter((_, i) => i !== index);
        onTiersChange(updatedTiers);
    };

    const handleTierChange = (index, field, value) => {
        const updatedTiers = tiers.map((tier, i) => 
            i === index ? { ...tier, [field]: value } : tier
        );
        onTiersChange(updatedTiers);
    };

    const renderMonetaryFields = (tier, index) => {
        return (
            <>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <InputLabel>Price Range</InputLabel>
                        <Select
                            value={tier.priceRange || ''}
                            label="Price Range"
                            onChange={(e) => handleTierChange(index, 'priceRange', e.target.value)}
                        >
                            {PRICE_RANGES.map((range) => (
                                <MenuItem key={range.value} value={range.value}>
                                    {range.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                {tier.priceRange === 'custom' && (
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Custom Price Range"
                            value={tier.customPriceRange}
                            onChange={(e) => handleTierChange(index, 'customPriceRange', e.target.value)}
                            placeholder="e.g., $75,000 - $90,000"
                        />
                    </Grid>
                )}
            </>
        );
    };

    const renderContributionFields = (tier, index) => {
        if (tier.contributionType === CONTRIBUTION_TYPES.MONETARY) {
            return renderMonetaryFields(tier, index);
        } else if (tier.contributionType === CONTRIBUTION_TYPES.IN_KIND) {
            return (
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="In-Kind Contribution Details"
                        value={tier.inKindDetails}
                        onChange={(e) => handleTierChange(index, 'inKindDetails', e.target.value)}
                        multiline
                        rows={2}
                        placeholder="Describe goods or services provided"
                    />
                </Grid>
            );
        }
        return null;
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Sponsor Tiers
            </Typography>

            {tiers.map((tier, index) => (
                <Paper key={index} elevation={2} sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1">Tier {index + 1}</Typography>
                        <IconButton onClick={() => handleRemoveTier(index)} color="error">
                            <DeleteIcon />
                        </IconButton>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Tier Name"
                                value={tier.name}
                                onChange={(e) => handleTierChange(index, 'name', e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Contribution Type</InputLabel>
                                <Select
                                    value={tier.contributionType || ''}
                                    label="Contribution Type"
                                    onChange={(e) => handleTierChange(index, 'contributionType', e.target.value)}
                                >
                                    <MenuItem value={CONTRIBUTION_TYPES.MONETARY}>Monetary</MenuItem>
                                    <MenuItem value={CONTRIBUTION_TYPES.IN_KIND}>In-Kind</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {renderContributionFields(tier, index)}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Custom Benefits or Requests"
                                value={tier.customBenefits}
                                onChange={(e) => handleTierChange(index, 'customBenefits', e.target.value)}
                                multiline
                                rows={2}
                                placeholder="List any specific benefits or requests"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={tier.isNegotiable}
                                        onChange={(e) => handleTierChange(index, 'isNegotiable', e.target.checked)}
                                    />
                                }
                                label="Terms are negotiable"
                            />
                        </Grid>
                    </Grid>
                </Paper>
            ))}

            <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Add New Tier
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Tier Name"
                            value={newTier.name}
                            onChange={(e) => setNewTier({ ...newTier, name: e.target.value })}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel>Contribution Type</InputLabel>
                            <Select
                                value={newTier.contributionType || ''}
                                label="Contribution Type"
                                onChange={(e) => setNewTier({ ...newTier, contributionType: e.target.value })}
                            >
                                <MenuItem value={CONTRIBUTION_TYPES.MONETARY}>Monetary</MenuItem>
                                <MenuItem value={CONTRIBUTION_TYPES.IN_KIND}>In-Kind</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {newTier.contributionType === CONTRIBUTION_TYPES.MONETARY && (
                        <>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Price Range</InputLabel>
                                    <Select
                                        value={newTier.priceRange || ''}
                                        label="Price Range"
                                        onChange={(e) => setNewTier({ ...newTier, priceRange: e.target.value })}
                                    >
                                        {PRICE_RANGES.map((range) => (
                                            <MenuItem key={range.value} value={range.value}>
                                                {range.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {newTier.priceRange === 'custom' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Custom Price Range"
                                        value={newTier.customPriceRange}
                                        onChange={(e) => setNewTier({ ...newTier, customPriceRange: e.target.value })}
                                        placeholder="e.g., $75,000 - $90,000"
                                    />
                                </Grid>
                            )}
                        </>
                    )}
                    {newTier.contributionType === CONTRIBUTION_TYPES.IN_KIND && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="In-Kind Contribution Details"
                                value={newTier.inKindDetails}
                                onChange={(e) => setNewTier({ ...newTier, inKindDetails: e.target.value })}
                                multiline
                                rows={2}
                                placeholder="Describe goods or services provided"
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Custom Benefits or Requests"
                            value={newTier.customBenefits}
                            onChange={(e) => setNewTier({ ...newTier, customBenefits: e.target.value })}
                            multiline
                            rows={2}
                            placeholder="List any specific benefits or requests"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={newTier.isNegotiable}
                                    onChange={(e) => setNewTier({ ...newTier, isNegotiable: e.target.checked })}
                                />
                            }
                            label="Terms are negotiable"
                        />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAddTier}
                        disabled={!newTier.name.trim() || !newTier.contributionType}
                    >
                        Add Tier
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default SponsorTierForm; 